import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { formatAmount } from "../utils/format";
import { TEXT } from "../utils/tokens";

/** 金額の文字の大きさ。lg=カードの金額（18px）、md=内訳の合計（16px）、sm=内訳の行（14px）。 */
type AmountSize = "lg" | "md" | "sm";

interface Props {
  amount: number | null;
  isProvisional: boolean;
  size?: AmountSize;
  /** 税込の金額であることを「税込」で示す。 */
  taxIncluded?: boolean;
  /**
   * 「暫定」「確定」のチップを出す。内訳の行では、カードの金額と注記が暫定であることを伝えるので出さない。
   */
  showChip?: boolean;
  /** PC でチップを金額の下の行に置く（カードの右列）。 */
  chipBelow?: boolean;
}

const SIZE: Record<AmountSize, { fontSize: number; fontWeight: number }> = {
  lg: { fontSize: TEXT.amount.fontSize, fontWeight: 700 },
  md: { fontSize: 16, fontWeight: 700 },
  sm: { fontSize: 14, fontWeight: 600 },
};

/** 「暫定」（灰色の outlined）または「確定」（success の薄地）のチップ。表のセル内にも置くため span で描画する。 */
export const AmountStatusChip: React.FC<{ isProvisional: boolean }> = ({
  isProvisional,
}) =>
  isProvisional ? (
    <Chip
      component="span"
      size="small"
      variant="outlined"
      label="暫定"
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: "text.secondary",
        borderColor: (theme) => theme.palette.border.main,
      }}
    />
  ) : (
    <Chip
      component="span"
      size="small"
      label="確定"
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        color: "success.dark",
        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
      }}
    />
  );

/**
 * 金額表示。金額（formatAmount の出力を1つのテキストノードで出す）、「税込」、暫定／確定のチップ。
 * 金額が未確定（null）のときは「金額未定」を 14px の text.secondary で出し、チップは付けない。
 * 表のセル（p 要素）の中にも置くため、すべて span で描画する。
 */
export const AmountText: React.FC<Props> = ({
  amount,
  isProvisional,
  size = "sm",
  taxIncluded = false,
  showChip = true,
  chipBelow = false,
}) => {
  if (amount === null) {
    return (
      <Typography
        component="span"
        sx={{ ...TEXT.body, color: "text.secondary" }}
      >
        {formatAmount(null)}
      </Typography>
    );
  }
  const figure = (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}
    >
      <Typography
        component="span"
        sx={{
          ...SIZE[size],
          lineHeight: 1.4,
          fontVariantNumeric: "tabular-nums",
          color: "text.primary",
          whiteSpace: "nowrap",
        }}
      >
        {formatAmount(amount)}
      </Typography>
      {taxIncluded && (
        <Typography
          component="span"
          sx={{ ...TEXT.meta, color: "text.secondary" }}
        >
          税込
        </Typography>
      )}
    </Box>
  );
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        // chipBelow は PC だけ縦に積む（SP の1列では金額の右にチップを置く）
        flexDirection: chipBelow
          ? { xs: "row", [MOBILE_BREAKPOINT]: "column" }
          : "row",
        alignItems: chipBelow
          ? { xs: "center", [MOBILE_BREAKPOINT]: "flex-end" }
          : "center",
        flexWrap: "wrap",
        gap: chipBelow ? { xs: 1, [MOBILE_BREAKPOINT]: 0.5 } : 1,
      }}
    >
      {figure}
      {showChip && <AmountStatusChip isProvisional={isProvisional} />}
    </Box>
  );
};
